import React, { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Bot, Send, X, Sparkles } from "lucide-react";

export default function Concierge() {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([
    { role: "bot", text: "Hi! I'm your AI Concierge 🌿 Tell me your ZIP, yard size, and what you need — I'll find the best-fit provider." },
  ]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [recs, setRecs] = useState([]);
  const sessionId = useRef(`sess_${Math.random().toString(36).slice(2)}`);
  const endRef = useRef(null);

  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages, open]);

  const send = async () => {
    if (!input.trim() || loading) return;
    const msg = input.trim();
    setMessages((m) => [...m, { role: "user", text: msg }]);
    setInput("");
    setLoading(true);
    try {
      const res = await api.post("/ai/concierge", { session_id: sessionId.current, message: msg });
      setMessages((m) => [...m, { role: "bot", text: res.data.reply }]);
      setRecs(res.data.recommended || []);
    } catch {
      setMessages((m) => [...m, { role: "bot", text: "Sorry, I hit a snag. Try our Search page to compare providers!" }]);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      {!open && (
        <button
          data-testid="concierge-open-btn"
          onClick={() => setOpen(true)}
          className="fixed bottom-6 right-6 z-50 w-14 h-14 rounded-full bg-lawn hover:bg-lawn-dark shadow-xl flex items-center justify-center text-white transition-transform hover:scale-105"
        >
          <Bot className="w-7 h-7" />
        </button>
      )}
      {open && (
        <div data-testid="concierge-panel" className="fixed bottom-6 right-6 z-50 w-[92vw] max-w-sm h-[540px] bg-white rounded-2xl shadow-2xl border border-border flex flex-col overflow-hidden">
          <div className="bg-lawn-ink text-white px-4 py-3 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-lg bg-lawn flex items-center justify-center"><Sparkles className="w-4 h-4" /></div>
              <div>
                <p className="font-display font-bold text-sm leading-none">AI Concierge</p>
                <p className="text-[11px] text-white/60 mt-0.5">Find & book in minutes</p>
              </div>
            </div>
            <button data-testid="concierge-close-btn" onClick={() => setOpen(false)}><X className="w-5 h-5" /></button>
          </div>
          <div className="flex-1 overflow-y-auto p-4 space-y-3 bg-muted/40">
            {messages.map((m, i) => (
              <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                <div className={`max-w-[85%] px-3 py-2 rounded-2xl text-sm leading-relaxed ${m.role === "user" ? "bg-lawn text-white rounded-br-sm" : "bg-white border border-border rounded-bl-sm"}`}>{m.text}</div>
              </div>
            ))}
            {recs.length > 0 && (
              <div className="space-y-2">
                {recs.map((r) => (
                  <button key={r.provider_id} data-testid={`concierge-rec-${r.provider_id}`} onClick={() => { setOpen(false); navigate(`/provider/${r.provider_id}`); }}
                    className="w-full text-left bg-white border border-lawn/30 rounded-xl px-3 py-2 hover:border-lawn transition-colors">
                    <p className="font-semibold text-sm text-lawn-ink">{r.name}</p>
                    <p className="text-xs text-muted-foreground">⭐ {r.rating} · {r.city}</p>
                  </button>
                ))}
              </div>
            )}
            {loading && <div className="text-xs text-muted-foreground">AI is thinking…</div>}
            <div ref={endRef} />
          </div>
          <div className="p-3 border-t border-border flex gap-2">
            <Input data-testid="concierge-input" value={input} onChange={(e) => setInput(e.target.value)} onKeyDown={(e) => e.key === "Enter" && send()} placeholder="e.g. Weekly mowing in 78701" className="rounded-full" />
            <Button data-testid="concierge-send-btn" onClick={send} disabled={loading} size="icon" className="rounded-full bg-lawn hover:bg-lawn-dark shrink-0"><Send className="w-4 h-4" /></Button>
          </div>
        </div>
      )}
    </>
  );
}
